/**
 * The date the user last generated a report for (product rule, 2026-08-06).
 *
 * After generating a report, reopening THAT report's configuration dialog
 * autofills the date it was just generated for and leaves every other field at
 * its default - so the same date can be rerun for a different portfolio with one
 * pick. Opening a DIFFERENT report wipes the memory: report B opens on the usual
 * yesterday/YTD defaults, and so does report A when the user comes back to it.
 *
 * The slot is deliberately SINGLE-ENTRY, and in memory only (never persisted):
 * "the date I just generated for" is one value, not a per-report history. A map
 * would resurrect a date the user has long since moved on from, which is exactly
 * what the wipe-on-switch half of the rule forbids.
 */

// The date keys buildReportRequestParams emits (lib/reportRequest.js): single-
// date reports carry `date`, range reports carry `date_range` {start, end}.
// Nothing else is remembered.
const DATE_KEYS = ['date', 'date_range'];

let slot = null;

/**
 * Record the dates a generation was submitted with. Called at every generation
 * seam (the Reports page popup and the report window's rerun dialog).
 */
export function rememberGeneratedDates(reportType, requestParams) {
  const dates = {};
  for (const key of DATE_KEYS) {
    const value = requestParams ? requestParams[key] : undefined;
    if (value !== undefined && value !== null) dates[key] = value;
  }
  slot = { reportType, dates };
}

/**
 * The remembered dates for this report type, or null when the last generation
 * was a different report (the dialog then falls back to its own defaults).
 *
 * Referentially stable until the next generation: the dialogs re-seed their
 * fields whenever their `initialParams` identity changes, so a fresh object per
 * call would wipe out what the user is typing.
 */
export function datePrefillFor(reportType) {
  return slot && slot.reportType === reportType ? slot.dates : null;
}

/** A dialog opened: a different report than the last generated one wipes the slot. */
export function noteReportPopupOpened(reportType) {
  if (slot && slot.reportType !== reportType) slot = null;
}

/** Test seam - the slot is module state and outlives a single render tree. */
export function clearGeneratedDates() {
  slot = null;
}
